import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import appSettings from '../../config/settings';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing, ensureUser } from '../../util/data';
import { getCoverImage } from '../../util/listingImages';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { getItemTypeLabel } from '../../util/productTypes';
import { getConditionCardLabel } from '../../util/productCondition';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, FavoriteButton, NamedLink, ResponsiveImage } from '../../components';
// empavision:enrichments — wired by hand; marker keeps `init` from re-wiring this file
import { BadgeStrip } from '../../extensions/empavision-enrichments';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

// Whole amounts without decimals on cards ("€700", Phase 2 mockup); cents stay ("€12.50").
const formatCardPrice = (intl, money) => {
  if (money.amount % 100 !== 0) {
    return formatMoney(intl, money);
  }
  const options = appSettings.getCurrencyFormatting(money.currency, {});
  return intl.formatNumber(money.amount / 100, {
    ...options,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatCardPrice(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

// Price before the latest drop. empavision writes publicData.priceDropAmount (minor units,
// same scale as price.amount) when a seller lowers the price.
const previousPriceMaybe = (price, publicData) => {
  const dropAmount = publicData?.priceDropAmount;
  return price && dropAmount > 0
    ? new Money(price.amount + dropAmount, price.currency)
    : null;
};

const IconCondition = () => (
  <svg className={css.metaIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 12.6V4a1 1 0 0 1 1-1h8.6a1 1 0 0 1 .7.3l7.4 7.4a1 1 0 0 1 0 1.4l-8.6 8.6a1 1 0 0 1-1.4 0l-7.4-7.4a1 1 0 0 1-.3-.7z" />
    <circle cx="7.5" cy="7.5" r="1.5" />
  </svg>
);

const IconLocation = () => (
  <svg className={css.metaIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const previousPrice = previousPriceMaybe(price, publicData);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {previousPrice ? (
        <s className={css.previousPrice}>{formatCardPrice(intl, previousPrice)}</s>
      ) : null}
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    currentUser,
    badges,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const isOwnListing = currentUser?.id?.uuid === author?.id?.uuid;
  const firstImage = getCoverImage(currentListing);

  const category = publicData.productCategory;
  const itemType = getItemTypeLabel(publicData.productItemType);
  const conditionLabel = getConditionCardLabel(publicData.productCondition);
  const county = publicData.locationCounty;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        <FavoriteButton
          listingId={currentListing.id}
          currentUser={currentUser}
          isOwnListing={isOwnListing}
          position="right"
          className={css.favorite}
        />
        <BadgeStrip enrichments={badges} className={css.badges} />
      </AspectRatioWrapper>
      <div className={css.info}>
        {category || itemType ? (
          <div className={css.categoryLine}>
            {[category, itemType].filter(Boolean).join(' · ')}
          </div>
        ) : null}

        <div className={css.title}>
          {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>

        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />

        {conditionLabel || county ? (
          <div className={css.meta}>
            {conditionLabel ? (
              <span className={css.metaItem}>
                <IconCondition />
                {conditionLabel}
              </span>
            ) : null}
            {county ? (
              <span className={css.metaItem}>
                <IconLocation />
                {county}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  currentUser: null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
  currentUser: propTypes.currentUser,
};

export default injectIntl(ListingCardComponent);
